// Cobro en tienda (opción A): resumen del crédito, tres pasos (cuánto, cómo, confirmar) y comprobante.
import { useState, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import type {
    SellerPaymentOption,
    SellerPaymentOptions,
    SellerPaymentResult,
} from '@/api/services/loan-credit';
import { Button, ErrorState, Input, NumberInput, Select, Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { ApiError } from '@/api/errors';
import { NoSourceBlock } from '@/shared/patterns/NoSource';
import { wireLookup } from '@/lib/wireLookup';
import { IN_STORE_METHOD_LABEL } from '@/domains/credit/payments/paymentOrigin';
import {
    chargeErrorOf,
    useCreateSellerPayment,
    useSellerPaymentOptions,
} from '../api/useSellerPayment';
import {
    chargeAmountOf,
    chargeDetailErrorOf,
    type ChargeDraft,
    type ChargeKind,
    type ChargePayment,
    type ChargeStep,
} from '../lib/chargeFlow';
import type { PendingCharge } from '../lib/pendingCharges';
import {
    cardDetailText,
    CHARGE_AMOUNT_LEGEND,
    CHARGE_BUTTON,
    CHARGE_CONFIRM_LABEL,
    CHARGE_CONFIRM_NOTE,
    CHARGE_FIELD_LABEL,
    CHARGE_KIND_TITLE,
    CHARGE_METHOD_LEGEND,
    CHARGE_OFFLINE_NOTE,
    CHARGE_OPTIONS_ERROR,
    CHARGE_OUT_OF_CHAIN_NOTE,
    CHARGE_STEP_LABEL,
    CHARGE_SUMMARY_LABEL,
    CHARGE_TITLE,
    CHARGE_UNAVAILABLE_FALLBACK,
    CHARGE_UNAVAILABLE_LABEL,
    dueDateLabel,
    installmentCountLabel,
    money,
    nextInstallmentDetail,
    PENDING_CHARGE_HINT,
    pendingChargeNotice,
    prepaymentRangeError,
    rangeText,
    speiDetailText,
} from '../lib/collectionsFormat';

type KindType = ChargeKind['type'];
type MethodType = ChargePayment['method'];

const FORM_STEPS: ChargeStep[] = ['amount', 'method', 'confirm'];
const METHOD_ORDER: MethodType[] = ['CASH', 'CARD', 'WIRE'];

function unavailableText(option: SellerPaymentOption): string {
    const reason = option.unavailableReason;
    return (reason && wireLookup(CHARGE_UNAVAILABLE_LABEL, reason)) ?? CHARGE_UNAVAILABLE_FALLBACK;
}

function paymentFor(method: MethodType): ChargePayment {
    if (method === 'CARD') return { method, cardLast4: '', authCode: '' };
    if (method === 'WIRE') return { method, speiTrackingKey: '' };
    return { method };
}

function defaultKind(type: KindType, options: SellerPaymentOptions): ChargeKind {
    if (type === 'ADVANCE') return { type, installmentCount: options.advance.minInstallments ?? 1 };
    if (type === 'PREPAYMENT') return { type, amountCents: options.prepayment.minAmountCents ?? 0 };
    return { type };
}

/** Error del abono fuera del rango del servidor; null si el tipo no es abono o está en rango. */
function prepaymentErrorOf(options: SellerPaymentOptions, kind: ChargeKind | null): string | null {
    if (kind?.type !== 'PREPAYMENT') return null;
    const min = options.prepayment.minAmountCents ?? 0;
    const max = options.prepayment.maxAmountCents ?? 0;
    if (kind.amountCents >= min && kind.amountCents <= max) return null;
    return prepaymentRangeError(money(min), money(max));
}

export interface ChargeFlowProps {
    creditId: string;
    /** El comprobante lo pinta quien monta el cobro: así sobrevive a que el crédito deje de cobrarse. */
    onCharged: (result: SellerPaymentResult) => void;
}

export function ChargeFlow({ creditId, onCharged }: ChargeFlowProps) {
    const options = useSellerPaymentOptions(creditId);
    const { charge, restartOffered, pendingOptionsFor, pendingCharge, discardPending } =
        useCreateSellerPayment(creditId);
    // El cobro sin respuesta que ya estaba al abrir: el vendedor elige antes de cobrar otra cosa.
    const [restored, setRestored] = useState<PendingCharge | null>(pendingCharge);
    const [step, setStep] = useState<ChargeStep>('amount');
    // Opciones congeladas al entrar a confirmar: un refetch no cambia el cuerpo ni la llave.
    const [frozenOptions, setFrozenOptions] = useState<SellerPaymentOptions | null>(null);
    const [kind, setKind] = useState<ChargeKind | null>(null);
    const [payment, setPayment] = useState<ChargePayment>({ method: 'CASH' });
    const [detailError, setDetailError] = useState<string | null>(null);

    if (options.isPending) return <Skeleton lines={5} />;
    if (options.error instanceof ApiError && options.error.code === 'CREDIT_NOT_FOUND') {
        return <NoSourceBlock>{CHARGE_OUT_OF_CHAIN_NOTE}</NoSourceBlock>;
    }
    if (options.isError) {
        return (
            <ErrorState description={CHARGE_OPTIONS_ERROR} onRetry={() => void options.refetch()} />
        );
    }
    const data = options.data;

    const confirmOptions = frozenOptions ?? data;
    const send = (draft: ChargeDraft, sendOptions: SellerPaymentOptions, forceNewKey: boolean) => {
        charge.mutate(
            { options: sendOptions, draft, forceNewKey },
            {
                onSuccess: (result) => {
                    charge.reset();
                    setKind(null);
                    setPayment({ method: 'CASH' });
                    setFrozenOptions(null);
                    setStep('amount');
                    onCharged(result);
                },
                onError: (error) => {
                    if (chargeErrorOf(error).action !== 'reload') return;
                    void options.refetch().then((reloaded) => {
                        if (reloaded.data) setFrozenOptions(reloaded.data);
                    });
                },
            },
        );
    };
    const submit = (forceNewKey: boolean) => {
        if (kind) send({ kind, payment }, confirmOptions, forceNewKey);
    };
    const retryRestored = (pending: PendingCharge) => {
        setKind(pending.draft.kind);
        setPayment(pending.draft.payment);
        setFrozenOptions(pending.options);
        setStep('confirm');
        setRestored(null);
        send(pending.draft, pending.options, false);
    };

    return (
        // flex-none: en el panel de columna el bloque se encogía y su overflow cortaba «Siguiente».
        <div
            data-charge-flow
            className="flex-none overflow-hidden rounded-xl border-2 border-primary bg-card"
        >
            <div className="px-3.5 pb-3 pt-2.5">
                <div className="mb-2 text-[10.5px] font-extrabold uppercase tracking-[0.08em] text-primary">
                    {CHARGE_TITLE}
                </div>
                <ChargeSummary options={data} />
                {restored ? (
                    <PendingChargeNotice
                        pending={restored}
                        onRetry={() => {
                            retryRestored(restored);
                        }}
                        onNewCharge={() => {
                            discardPending();
                            setRestored(null);
                        }}
                    />
                ) : (
                    <StepTrail current={step} />
                )}
                {!restored && step === 'amount' ? (
                    <AmountStep
                        options={data}
                        kind={kind}
                        onKindChange={setKind}
                        onNext={() => {
                            charge.reset();
                            setStep('method');
                        }}
                    />
                ) : null}
                {!restored && step === 'method' ? (
                    <MethodStep
                        payment={payment}
                        detailError={detailError}
                        onPaymentChange={(next) => {
                            setPayment(next);
                            setDetailError(null);
                        }}
                        onBack={() => {
                            setStep('amount');
                        }}
                        onNext={() => {
                            const error = chargeDetailErrorOf(payment);
                            setDetailError(error);
                            if (!error && kind) {
                                charge.reset();
                                setFrozenOptions(pendingOptionsFor({ kind, payment }) ?? data);
                                setStep('confirm');
                            }
                        }}
                    />
                ) : null}
                {!restored && step === 'confirm' && kind ? (
                    <ConfirmStep
                        options={confirmOptions}
                        kind={kind}
                        payment={payment}
                        isSending={charge.isPending}
                        isPaused={charge.isPaused}
                        isReloading={options.isFetching}
                        error={charge.isError ? charge.error : null}
                        restartOffered={restartOffered}
                        onConfirm={() => {
                            submit(false);
                        }}
                        onRestart={() => {
                            submit(true);
                        }}
                        onFix={() => {
                            setStep('method');
                        }}
                        onChangeAmount={() => {
                            setStep('amount');
                        }}
                    />
                ) : null}
            </div>
        </div>
    );
}

function PendingChargeNotice({
    pending,
    onRetry,
    onNewCharge,
}: {
    pending: PendingCharge;
    onRetry: () => void;
    onNewCharge: () => void;
}) {
    return (
        <div
            role="alert"
            className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-[12px]"
        >
            <p className="font-semibold">
                {pendingChargeNotice(pending.amountCents, pending.createdAt)}
            </p>
            <p className="mt-1 text-[11.5px] text-muted-foreground">{PENDING_CHARGE_HINT}</p>
            <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" onClick={onRetry}>
                    {CHARGE_BUTTON.retryPending}
                </Button>
                <Button size="sm" variant="outline" onClick={onNewCharge}>
                    {CHARGE_BUTTON.newCharge}
                </Button>
            </div>
        </div>
    );
}

function ChargeSummary({ options }: { options: SellerPaymentOptions }) {
    const { summary } = options;
    const next = summary.nextInstallment;
    const items: [string, ReactNode][] = [
        [CHARGE_SUMMARY_LABEL.total, money(summary.totalCents)],
        [CHARGE_SUMMARY_LABEL.paid, money(summary.paidCents)],
        [CHARGE_SUMMARY_LABEL.remaining, money(summary.remainingCents)],
        [
            CHARGE_SUMMARY_LABEL.next,
            next ? `${money(next.amountCents)} · ${dueDateLabel(next.dueDate ?? undefined)}` : '—',
        ],
    ];
    return (
        <dl className="mb-3 grid grid-cols-4 gap-2 rounded-lg bg-muted/30 px-3 py-2">
            {items.map(([label, value]) => (
                <div key={label}>
                    <dt className="text-[9.5px] font-bold uppercase tracking-wide text-muted-foreground">
                        {label}
                    </dt>
                    <dd className="truncate text-[12.5px] font-bold tabular-nums">{value}</dd>
                </div>
            ))}
        </dl>
    );
}

function StepTrail({ current }: { current: ChargeStep }) {
    const currentIndex = FORM_STEPS.indexOf(current);
    return (
        <ol className="mb-2.5 flex gap-3 text-[11px] font-semibold">
            {FORM_STEPS.map((key, index) => (
                <li
                    key={key}
                    aria-current={index === currentIndex ? 'step' : undefined}
                    className={cn(
                        'flex items-center gap-1',
                        index === currentIndex ? 'text-primary' : 'text-muted-foreground',
                    )}
                >
                    <span
                        aria-hidden
                        className={cn(
                            'flex size-4 items-center justify-center rounded-full border text-[9.5px]',
                            index <= currentIndex
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border',
                        )}
                    >
                        {index + 1}
                    </span>
                    {CHARGE_STEP_LABEL[key]}
                </li>
            ))}
        </ol>
    );
}

interface ChoiceProps {
    checked: boolean;
    disabled?: boolean;
    onSelect: () => void;
    title: string;
    detail?: string;
    aside?: string;
}

/** Fila elegible con rol de radio. */
function Choice({ checked, disabled, onSelect, title, detail, aside }: ChoiceProps) {
    return (
        <button
            type="button"
            role="radio"
            aria-checked={checked}
            disabled={disabled}
            onClick={onSelect}
            className={cn(
                'flex w-full items-start gap-2.5 rounded-lg border px-3 py-2 text-left text-[12.5px] transition-colors',
                checked ? 'border-primary bg-primary/5' : 'border-border bg-card',
                disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-primary/60',
            )}
        >
            <span
                aria-hidden
                className={cn(
                    'mt-0.5 flex size-4 flex-none items-center justify-center rounded-full border',
                    checked ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
                )}
            >
                {checked ? <Check className="size-3" /> : null}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
                <span className="font-semibold">{title}</span>
                {detail ? (
                    <span className="text-[11px] text-muted-foreground">{detail}</span>
                ) : null}
            </span>
            {aside ? <span className="flex-none font-bold tabular-nums">{aside}</span> : null}
        </button>
    );
}

interface AmountStepProps {
    options: SellerPaymentOptions;
    kind: ChargeKind | null;
    onKindChange: (kind: ChargeKind) => void;
    onNext: () => void;
}

function AmountStep({ options, kind, onKindChange, onNext }: AmountStepProps) {
    const { nextInstallment, advance, prepayment, settlement } = options;
    const select = (type: KindType) => {
        if (kind?.type !== type) onKindChange(defaultKind(type, options));
    };
    const rangeError = prepaymentErrorOf(options, kind);
    const advanceCounts = Array.from(
        {
            length: Math.max(
                0,
                (advance.maxInstallments ?? 0) - (advance.minInstallments ?? 0) + 1,
            ),
        },
        (_, index) => (advance.minInstallments ?? 0) + index,
    );
    return (
        <div>
            <div
                role="radiogroup"
                aria-label={CHARGE_AMOUNT_LEGEND}
                className="flex flex-col gap-1.5"
            >
                <Choice
                    title={CHARGE_KIND_TITLE.NEXT_INSTALLMENT}
                    checked={kind?.type === 'NEXT_INSTALLMENT'}
                    disabled={!nextInstallment.available}
                    onSelect={() => {
                        select('NEXT_INSTALLMENT');
                    }}
                    detail={
                        nextInstallment.available
                            ? nextInstallmentDetail(
                                  nextInstallment.installmentNumbers,
                                  nextInstallment.overdueCount,
                              )
                            : unavailableText(nextInstallment)
                    }
                    aside={
                        nextInstallment.available
                            ? money(nextInstallment.amountCents ?? 0)
                            : undefined
                    }
                />
                <Choice
                    title={CHARGE_KIND_TITLE.ADVANCE}
                    checked={kind?.type === 'ADVANCE'}
                    disabled={!advance.available}
                    onSelect={() => {
                        select('ADVANCE');
                    }}
                    detail={
                        advance.available
                            ? rangeText(
                                  installmentCountLabel(advance.minInstallments ?? 0),
                                  installmentCountLabel(advance.maxInstallments ?? 0),
                              )
                            : unavailableText(advance)
                    }
                    aside={
                        kind?.type === 'ADVANCE' ? money(chargeAmountOf(options, kind)) : undefined
                    }
                />
                {kind?.type === 'ADVANCE' ? (
                    <div className="pl-9">
                        <Select
                            label={CHARGE_FIELD_LABEL.installmentCount}
                            value={String(kind.installmentCount)}
                            onChange={(value) => {
                                onKindChange({ type: 'ADVANCE', installmentCount: Number(value) });
                            }}
                            options={advanceCounts.map((count) => ({
                                value: String(count),
                                label: installmentCountLabel(count),
                            }))}
                        />
                    </div>
                ) : null}
                <Choice
                    title={CHARGE_KIND_TITLE.PREPAYMENT}
                    checked={kind?.type === 'PREPAYMENT'}
                    disabled={!prepayment.available}
                    onSelect={() => {
                        select('PREPAYMENT');
                    }}
                    detail={
                        prepayment.available
                            ? rangeText(
                                  money(prepayment.minAmountCents ?? 0),
                                  money(prepayment.maxAmountCents ?? 0),
                              )
                            : unavailableText(prepayment)
                    }
                />
                {kind?.type === 'PREPAYMENT' ? (
                    <div className="pl-9">
                        <div className="relative w-40">
                            <span
                                aria-hidden
                                className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-sm text-muted-foreground"
                            >
                                $
                            </span>
                            <NumberInput
                                aria-label={CHARGE_FIELD_LABEL.prepaymentAmount}
                                value={kind.amountCents / 100}
                                step={0.01}
                                min={(prepayment.minAmountCents ?? 0) / 100}
                                max={(prepayment.maxAmountCents ?? 0) / 100}
                                onValueChange={(pesos) => {
                                    onKindChange({
                                        type: 'PREPAYMENT',
                                        amountCents: Math.round(pesos * 100),
                                    });
                                }}
                                className="w-full pl-5 text-sm"
                            />
                        </div>
                        {rangeError ? (
                            <p className="mt-1 text-[11px] text-destructive">{rangeError}</p>
                        ) : null}
                    </div>
                ) : null}
                <Choice
                    title={CHARGE_KIND_TITLE.SETTLEMENT}
                    checked={kind?.type === 'SETTLEMENT'}
                    disabled={!settlement.available}
                    onSelect={() => {
                        select('SETTLEMENT');
                    }}
                    detail={settlement.available ? undefined : unavailableText(settlement)}
                    aside={settlement.available ? money(settlement.amountCents ?? 0) : undefined}
                />
            </div>
            <div className="mt-3 flex justify-end">
                <Button size="sm" disabled={!kind || rangeError !== null} onClick={onNext}>
                    {CHARGE_BUTTON.next}
                </Button>
            </div>
        </div>
    );
}

interface MethodStepProps {
    payment: ChargePayment;
    detailError: string | null;
    onPaymentChange: (payment: ChargePayment) => void;
    onBack: () => void;
    onNext: () => void;
}

function MethodStep({ payment, detailError, onPaymentChange, onBack, onNext }: MethodStepProps) {
    return (
        <div>
            <div
                role="radiogroup"
                aria-label={CHARGE_METHOD_LEGEND}
                className="grid grid-cols-3 gap-1.5"
            >
                {METHOD_ORDER.map((method) => (
                    <Choice
                        key={method}
                        title={IN_STORE_METHOD_LABEL[method]}
                        checked={payment.method === method}
                        onSelect={() => {
                            if (payment.method !== method) onPaymentChange(paymentFor(method));
                        }}
                    />
                ))}
            </div>
            {payment.method === 'CARD' ? (
                <div className="mt-2.5 grid grid-cols-2 gap-2">
                    <Input
                        label={CHARGE_FIELD_LABEL.cardLast4}
                        inputMode="numeric"
                        maxLength={4}
                        value={payment.cardLast4}
                        onChange={(event) => {
                            onPaymentChange({ ...payment, cardLast4: event.target.value });
                        }}
                    />
                    <Input
                        label={CHARGE_FIELD_LABEL.authCode}
                        maxLength={12}
                        value={payment.authCode}
                        onChange={(event) => {
                            onPaymentChange({ ...payment, authCode: event.target.value });
                        }}
                    />
                </div>
            ) : null}
            {payment.method === 'WIRE' ? (
                <div className="mt-2.5">
                    <Input
                        label={CHARGE_FIELD_LABEL.speiTrackingKey}
                        value={payment.speiTrackingKey}
                        onChange={(event) => {
                            onPaymentChange({ ...payment, speiTrackingKey: event.target.value });
                        }}
                    />
                </div>
            ) : null}
            {detailError ? (
                <p role="alert" className="mt-2 text-[11.5px] font-semibold text-destructive">
                    {detailError}
                </p>
            ) : null}
            <div className="mt-3 flex justify-between">
                <Button size="sm" variant="ghost" onClick={onBack}>
                    {CHARGE_BUTTON.back}
                </Button>
                <Button size="sm" onClick={onNext}>
                    {CHARGE_BUTTON.next}
                </Button>
            </div>
        </div>
    );
}

function paymentDetailText(payment: ChargePayment): string | null {
    if (payment.method === 'CARD') return cardDetailText(payment.cardLast4, payment.authCode);
    if (payment.method === 'WIRE') return speiDetailText(payment.speiTrackingKey.trim());
    return null;
}

function kindText(kind: ChargeKind): string {
    if (kind.type === 'ADVANCE') {
        return `${CHARGE_KIND_TITLE.ADVANCE} · ${installmentCountLabel(kind.installmentCount)}`;
    }
    return CHARGE_KIND_TITLE[kind.type];
}

interface ConfirmStepProps {
    options: SellerPaymentOptions;
    kind: ChargeKind;
    payment: ChargePayment;
    isSending: boolean;
    isPaused: boolean;
    isReloading: boolean;
    error: unknown;
    restartOffered: boolean;
    onConfirm: () => void;
    onRestart: () => void;
    onFix: () => void;
    onChangeAmount: () => void;
}

function ConfirmStep({
    options,
    kind,
    payment,
    isSending,
    isPaused,
    isReloading,
    error,
    restartOffered,
    onConfirm,
    onRestart,
    onFix,
    onChangeAmount,
}: ConfirmStepProps) {
    const amountCents = chargeAmountOf(options, kind);
    const failure = error === null ? null : chargeErrorOf(error);
    const detail = paymentDetailText(payment);
    const stopped = failure?.action === 'stop';
    return (
        <div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-lg bg-muted/30 px-3 py-2 text-[12px]">
                <dt className="text-muted-foreground">{CHARGE_CONFIRM_LABEL.kind}</dt>
                <dd className="text-right font-semibold">{kindText(kind)}</dd>
                <dt className="text-muted-foreground">{CHARGE_CONFIRM_LABEL.amount}</dt>
                <dd className="text-right font-bold tabular-nums">{money(amountCents)}</dd>
                <dt className="text-muted-foreground">{CHARGE_CONFIRM_LABEL.method}</dt>
                <dd className="text-right font-semibold">
                    {IN_STORE_METHOD_LABEL[payment.method]}
                    {detail ? (
                        <span className="block text-[11px] font-normal text-muted-foreground">
                            {detail}
                        </span>
                    ) : null}
                </dd>
            </dl>
            <p className="mt-2 text-[11px] text-muted-foreground">{CHARGE_CONFIRM_NOTE}</p>
            {isPaused ? (
                <p
                    role="status"
                    className="mt-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-[11.5px] font-semibold"
                >
                    {CHARGE_OFFLINE_NOTE}
                </p>
            ) : null}
            {failure ? (
                <div
                    role="alert"
                    className="mt-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[11.5px] font-semibold text-destructive"
                >
                    {failure.message}
                    <div className="mt-1.5 flex flex-wrap gap-2">
                        {restartOffered ? (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={onRestart}
                                disabled={isSending}
                            >
                                {CHARGE_BUTTON.restart}
                            </Button>
                        ) : null}
                        {failure.action === 'fix' ? (
                            <Button size="sm" variant="outline" onClick={onFix}>
                                {CHARGE_BUTTON.fixDetail}
                            </Button>
                        ) : null}
                        {failure.action === 'reload' ? (
                            <Button size="sm" variant="outline" onClick={onChangeAmount}>
                                {CHARGE_BUTTON.changeAmount}
                            </Button>
                        ) : null}
                    </div>
                </div>
            ) : null}
            <div className="mt-3 flex justify-between">
                <Button size="sm" variant="ghost" onClick={onFix} disabled={isSending}>
                    {CHARGE_BUTTON.back}
                </Button>
                <Button
                    size="sm"
                    isLoading={isSending}
                    disabled={stopped || isReloading}
                    onClick={onConfirm}
                >
                    {`${CHARGE_BUTTON.confirm} ${money(amountCents)}`}
                </Button>
            </div>
        </div>
    );
}
